import React, { useEffect, useRef } from 'react';
import { styled } from '@uhg-abyss/web/tools/styled';
import { V2Card } from '@uhg-abyss/web/ui/Card';
import { V2Badge } from '@uhg-abyss/web/ui/Badge';
import { V2Alert } from '@uhg-abyss/web/ui/Alert';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { V2TextInput } from '@uhg-abyss/web/ui/TextInput';
import { V2SelectInput } from '@uhg-abyss/web/ui/SelectInput';
import { useCollapse } from '@uhg-abyss/web/hooks/useCollapse';
import { STATE_LIST } from '@src/common/stateOptions';
import { LocationProofUpload } from './LocationProofUpload';

const DOC360_UPLOAD_ERROR_ALERT_CSS = {
  'abyss-alert-root': { width: '100%', maxWidth: '380px' },
};
const ERROR_CARD_CSS = {
  'abyss-card-root': { borderColor: '$error1' },
};

const SectionHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '12px',
  cursor: 'pointer',
});

const SectionHeadingGroup = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
});

const FieldGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  margin: '16px 4px 0',
});

const UploadGroup = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  margin: '16px 4px 0',
});

const CityStateZipRow = styled('div', {
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-start',
});

const CardHeader = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
});

const CardTitle = styled('span', {
  fontSize: '16px',
  fontWeight: 700,
  color: '$primary1',
});

const HeaderActions = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
});

const SectionText = styled('p', {
  margin: '8px 0 8px',
  fontSize: '16px',
  fontWeight: 600,
});

const FlexColWide = styled('div', { flex: 2 });
const FlexColNarrow = styled('div', { flex: 1 });

const streetFormatter = (value) =>
  value.replace(/[&/\\#,+()$~%.'":*?<>{}]/g, '');

const cityFormatter = (value) =>
  value.replace(/[!@#$%^&*)({}<>,.\[\]+_`~=\\|';":/?0-9]/g, '');

const CITY_VALIDATORS = {
  required: 'City is required.',
  validate: {
    isValidCity: (value) =>
      !value.match(/[a-zA-Z]/g) ? 'Please enter a valid city name.' : null,
  },
};

const ZIP_VALIDATORS = {
  required: 'ZIP code is required.',
  validate: {
    isValidZip: (value) =>
      !/^\d{5}$/.test(value) ? 'Please enter a valid ZIP code.' : null,
  },
};

const REVALIDATE_DEBOUNCE_MS = 150;

const AddressFields = ({ modelPrefix, beforeFields, triggerAll }) => {
  const debounceTimerRef = useRef(null);

  const runTriggerAll = () => {
    triggerAll?.();
  };

  const handleFieldChange = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      runTriggerAll();
    }, REVALIDATE_DEBOUNCE_MS);
  };

  const handleFieldBlur = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    runTriggerAll();
  };

  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    },
    []
  );

  return (
    <FieldGroup>
      {beforeFields}
      <V2TextInput
        label="Address line 1"
        model={`${modelPrefix}.streetLine1`}
        validators={{ required: 'Address line 1 is required.' }}
        formatter={streetFormatter}
        maxLength={80}
        isClearable
        autoComplete="none"
        onChange={handleFieldChange}
        onBlur={handleFieldBlur}
      />

      <V2TextInput
        label="Address line 2"
        model={`${modelPrefix}.streetLine2`}
        formatter={streetFormatter}
        maxLength={80}
        isClearable
        autoComplete="none"
        onChange={handleFieldChange}
        onBlur={handleFieldBlur}
      />

      <CityStateZipRow>
        <FlexColWide>
          <V2TextInput
            label="City"
            model={`${modelPrefix}.city`}
            validators={CITY_VALIDATORS}
            formatter={cityFormatter}
            maxLength={80}
            isClearable
            autoComplete="none"
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
          />
        </FlexColWide>

        <FlexColWide>
          <V2SelectInput
            label="State"
            model={`${modelPrefix}.state`}
            placeholder="Select One"
            isSearchable
            isClearable
            options={STATE_LIST}
            validators={{ required: 'State is required.' }}
            autoComplete="none"
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
          />
        </FlexColWide>

        <FlexColNarrow>
          <V2TextInput
            label="ZIP code"
            model={`${modelPrefix}.zipCode`}
            mask="zip"
            validators={ZIP_VALIDATORS}
            isClearable
            autoComplete="none"
            onChange={handleFieldChange}
            onBlur={handleFieldBlur}
          />
        </FlexColNarrow>
      </CityStateZipRow>
    </FieldGroup>
  );
};

export const StaticAddressSection = ({
  title,
  helperText,
  helperText2,
  beforeFields,
  modelPrefix,
  proofModel,
  proofLabel,
  doc360Error,
  setDoc360UploadErrors,
  hasError,
  addrValidationCount,
  triggerAll,
}) => {
  const { collapseProps, buttonProps, isOpen, setIsOpen } = useCollapse({
    defaultIsOpen: true,
  });

  useEffect(() => {
    if (hasError) {
      setIsOpen(true);
    }
  }, [hasError, addrValidationCount]);

  return (
    <V2Card css={hasError && ERROR_CARD_CSS}>
      <SectionHeader {...buttonProps}>
        <SectionHeadingGroup>
          <h4>{title}</h4>
        </SectionHeadingGroup>
        <HeaderActions>
          <V2Badge variant="warning" outline>
            Required
          </V2Badge>
          <IconSymbol
            icon={isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            size="24px"
          />
        </HeaderActions>
      </SectionHeader>
      <SectionText>
        {helperText}
        {helperText2 && (
          <>
            <br />
            {helperText2}
          </>
        )}
      </SectionText>
      <div {...collapseProps}>
        <AddressFields
          modelPrefix={modelPrefix}
          beforeFields={beforeFields}
          triggerAll={triggerAll}
        />
        <UploadGroup>
          <LocationProofUpload
            model={proofModel}
            label={proofLabel}
            validators={{ required: `${proofLabel} is required.` }}
            setDoc360UploadErrors={setDoc360UploadErrors}
            onProofStateChange={triggerAll}
          />
          {doc360Error && (
            <V2Alert
              variant="error"
              dismissible={false}
              inline
              css={DOC360_UPLOAD_ERROR_ALERT_CSS}
            >
              {doc360Error}
            </V2Alert>
          )}
        </UploadGroup>
      </div>
    </V2Card>
  );
};

export const SecondaryAddressCard = ({
  addrId,
  addrNumber,
  totalAddresses,
  onAddrRemove,
  hasError,
  doc360Error,
  setDoc360UploadErrors,
  addrValidationCount,
  triggerAll,
}) => {
  const { collapseProps, buttonProps, isOpen, setIsOpen } = useCollapse({
    defaultIsOpen: true,
  });

  const proofModel = `address.secondaryServicing[${addrId}].proof`;

  useEffect(() => {
    if (hasError) {
      setIsOpen(true);
    }
  }, [hasError, addrValidationCount]);

  return (
    <V2Card css={hasError && ERROR_CARD_CSS}>
      <CardHeader {...buttonProps}>
        <CardTitle>
          Address ({addrNumber}/{totalAddresses})
        </CardTitle>
        <HeaderActions>
          <IconSymbol
            icon="delete"
            size="24px"
            variant="outlined"
            onClick={() => onAddrRemove(addrId)}
          />
          <IconSymbol
            icon={isOpen ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
            size="24px"
          />
        </HeaderActions>
      </CardHeader>

      <div {...collapseProps}>
        <AddressFields
          modelPrefix={`address.secondaryServicing[${addrId}]`}
          triggerAll={triggerAll}
        />
        <UploadGroup>
          <LocationProofUpload
            model={proofModel}
            label="Proof of servicing address"
            validators={{ required: 'Proof of servicing address is required.' }}
            setDoc360UploadErrors={setDoc360UploadErrors}
            onProofStateChange={triggerAll}
          />
          {doc360Error && (
            <V2Alert
              variant="error"
              dismissible={false}
              inline
              css={DOC360_UPLOAD_ERROR_ALERT_CSS}
            >
              {doc360Error}
            </V2Alert>
          )}
        </UploadGroup>
      </div>
    </V2Card>
  );
};
